import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateReconciliationInput,
  MatchInput,
  ResolveInput,
} from "@/modules/wallet/domain/repo/reconciliation";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { reconciliationApi } from "./reconciliation-client";

export const reconciliationKeys = {
  all: ["reconciliation"] as const,
  stats: () => ["reconciliation", "stats"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["reconciliation", "list", q] as const,
  detail: (id: string) => ["reconciliation", "detail", id] as const,
};

export function useReconciliationStatsQuery() {
  return useQuery({
    queryKey: reconciliationKeys.stats(),
    queryFn: () => reconciliationApi.getStats(),
  });
}

export function useReconciliationListQuery(
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: reconciliationKeys.list(q),
    queryFn: () => reconciliationApi.list(q),
  });
}

export function useReconciliationDetailQuery(id: string) {
  return useQuery({
    queryKey: reconciliationKeys.detail(id),
    queryFn: () => reconciliationApi.getDetail(id),
    enabled: !!id,
  });
}

export function useImportReconciliationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (records: CreateReconciliationInput[]) =>
      reconciliationApi.importRecords(records),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: reconciliationKeys.all });
      toast.success(`ນຳເຂົ້າ ${data.count} ລາຍການສຳເລັດ`);
    },
    onError: () => {
      toast.error("ນຳເຂົ້າລົ້ມເຫຼວ");
    },
  });
}

export function useMatchReconciliationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MatchInput }) =>
      reconciliationApi.match(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reconciliationKeys.all });
      toast.success("ຈັບຄູ່ສຳເລັດ");
    },
    onError: () => {
      toast.error("ຈັບຄູ່ລົ້ມເຫຼວ");
    },
  });
}

export function useResolveReconciliationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ResolveInput }) =>
      reconciliationApi.resolve(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reconciliationKeys.all });
      toast.success("ແກ້ໄຂສຳເລັດ");
    },
    onError: () => {
      toast.error("ແກ້ໄຂລົ້ມເຫຼວ");
    },
  });
}
